import { useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import { Bus, MapPin, Users, Calendar, Phone, Mail, MessageSquare, CheckCircle, ArrowRight, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import axios from 'axios';

export default function GetQuote() {
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '', email: '', phone: '',
        fromLocation: '', destination: '', stayLocation: '', travelDate: '',
        passengers: '', vehiclePreference: '', notes: '',
    });

    const handleChange = e => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${process.env.NEXT_PUBLIC_API_URL}/api/quotes`, formData, {
                headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            if (response.status === 201) {
                setSubmitted(true);
            }
        } catch (error) {
            console.error('Quote submission error:', error);
            alert('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const today = new Date().toISOString().split('T')[0];

    if (submitted) return (
        <div className="min-vh-100 bg-gray-50 flex items-center justify-center p-6">
            <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 max-w-lg w-full text-center animate-in fade-in zoom-in duration-500">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <h2 className="text-3xl font-black text-gray-900 mb-3 tracking-tight">Request Received!</h2>
                <p className="text-gray-600 mb-2 leading-relaxed">
                    Great news, <span className="font-bold text-gray-900">{formData.name}</span>! Our team is already working on your custom travel plan.
                </p>
                <p className="text-gray-500 mb-8 text-sm">
                    A detailed PDF quote will be sent to <span className="font-semibold text-green-700 underline decoration-green-200 underline-offset-4">{formData.email}</span> within the next 2 hours.
                </p>
                <Link href="/" className="inline-flex items-center justify-center gap-2 w-full bg-green-600 text-white px-8 py-4 rounded-2xl font-bold hover:bg-green-700 transition-all shadow-lg shadow-green-600/20 active:scale-95">
                    Back to Homepage <ArrowRight className="h-4 w-4" />
                </Link>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50 font-sans">
            <Head><title>Instant Custom Quote - EzyTravel</title></Head>

            <Header />

            {/* Hero Section */}
            <div className="bg-white border-b border-gray-100">
                <div className="max-w-7xl mx-auto py-12 md:py-16 px-6 text-center">
                    <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest mb-4">
                       <MessageSquare className="h-3 w-3" /> Get a Quote
                    </div>
                    <h1 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 tracking-tight">Your Journey, <span className="text-green-600 italic">Redefined.</span></h1>
                    <p className="text-gray-500 text-lg max-w-2xl mx-auto leading-relaxed">
                        No fixed routes. No compromises. Tell us where you want to go, and we'll handle the rest with a custom-priced plan.
                    </p>
                </div>
            </div>

            <main className="max-w-4xl mx-auto py-12 px-6">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
                    {/* Form Side */}
                    <div className="lg:col-span-3">
                        <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/50 p-6 md:p-8 border border-gray-100">
                            <h2 className="text-xl font-bold text-gray-900 mb-8 border-l-4 border-green-600 pl-4 uppercase tracking-tight">Travel Details</h2>
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Service Preference FIRST */}
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-widest">Which service do you need?</label>
                                    <div className="relative group">
                                        <Bus className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                                        <select name="vehiclePreference" value={formData.vehiclePreference} onChange={handleChange} required
                                            className="appearance-none pl-12 w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4 text-sm font-semibold text-gray-700 transition-all focus:ring-4 focus:ring-green-600/10 focus:border-green-600 outline-none">
                                            <option value="">Select Service Type</option>
                                            <option value="bus">Luxury Bus (30-50 seats)</option>
                                            <option value="minibus">Minibus / Tempo (15-25 seats)</option>
                                            <option value="car">Private Car / SUV</option>
                                            <option value="stay">Hotel / Resort Stay</option>
                                            <option value="luxury">VIP Luxury Coach</option>
                                        </select>
                                    </div>
                                </div>

                                <hr className="border-gray-50" />

                                {/* Personal Info */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-widest">Full Name</label>
                                        <div className="relative group">
                                            <Users className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                                            <input required name="name" type="text" placeholder="John Doe" value={formData.name} onChange={handleChange}
                                                className="pl-12 w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4 text-sm font-semibold text-gray-700 transition-all focus:ring-4 focus:ring-green-600/10 focus:border-green-600 outline-none placeholder-gray-300" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-widest">Phone Number</label>
                                        <div className="relative group">
                                            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                                            <input required name="phone" type="tel" placeholder="+91 XXXXXXXXXX" value={formData.phone} onChange={handleChange}
                                                className="pl-12 w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4 text-sm font-semibold text-gray-700 transition-all focus:ring-4 focus:ring-green-600/10 focus:border-green-600 outline-none placeholder-gray-300" />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-widest">Email for the Quote</label>
                                    <div className="relative group">
                                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                                        <input required name="email" type="email" placeholder="you@example.com" value={formData.email} onChange={handleChange}
                                            className="pl-12 w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4 text-sm font-semibold text-gray-700 transition-all focus:ring-4 focus:ring-green-600/10 focus:border-green-600 outline-none placeholder-gray-300" />
                                    </div>
                                </div>

                                {/* Dynamic Fields */}
                                {formData.vehiclePreference === 'stay' ? (
                                    <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-widest">Location of Interest</label>
                                        <div className="relative group">
                                            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                                            <input required name="stayLocation" type="text" placeholder="e.g., Manali, Goa, Rishikesh" value={formData.stayLocation} onChange={handleChange}
                                                className="pl-12 w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4 text-sm font-semibold text-gray-700 transition-all focus:ring-4 focus:ring-green-600/10 focus:border-green-600 outline-none" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 animate-in fade-in slide-in-from-top-2 duration-300">
                                        <div>
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-widest">From</label>
                                            <div className="relative group">
                                                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                                                <input required name="fromLocation" type="text" placeholder="Departure city" value={formData.fromLocation} onChange={handleChange}
                                                    className="pl-12 w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4 text-sm font-semibold text-gray-700 transition-all focus:ring-4 focus:ring-green-600/10 focus:border-green-600 outline-none" />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-widest">To</label>
                                            <div className="relative group">
                                                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                                                <input required name="destination" type="text" placeholder="Arrival city" value={formData.destination} onChange={handleChange}
                                                    className="pl-12 w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4 text-sm font-semibold text-gray-700 transition-all focus:ring-4 focus:ring-green-600/10 focus:border-green-600 outline-none" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-widest">{formData.vehiclePreference === 'stay' ? 'Check-in Date' : 'Travel Date'}</label>
                                        <div className="relative group">
                                            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                                            <input required name="travelDate" type="date" min={today} value={formData.travelDate} onChange={handleChange}
                                                className="pl-12 w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4 text-sm font-semibold text-gray-700 transition-all focus:ring-4 focus:ring-green-600/10 focus:border-green-600 outline-none" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-widest">Total Guests</label>
                                        <div className="relative group">
                                            <Users className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                                            <input required name="passengers" type="number" min="1" placeholder="e.g. 10" value={formData.passengers} onChange={handleChange}
                                                className="pl-12 w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4 text-sm font-semibold text-gray-700 transition-all focus:ring-4 focus:ring-green-600/10 focus:border-green-600 outline-none" />
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase mb-2 tracking-widest">Extra Requirements</label>
                                    <div className="relative group">
                                        <MessageSquare className="absolute left-4 top-5 h-5 w-5 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                                        <textarea name="notes" rows="4" placeholder="Mention any specific routes, stops, or luxury requirements..." value={formData.notes} onChange={handleChange}
                                            className="pl-12 w-full bg-gray-50 border border-gray-200 rounded-2xl px-4 py-4 text-sm font-semibold text-gray-700 transition-all focus:ring-4 focus:ring-green-600/10 focus:border-green-600 outline-none placeholder-gray-300" />
                                    </div>
                                </div>

                                <button type="submit" disabled={loading}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white font-black py-5 rounded-2xl transition-all text-sm uppercase tracking-widest shadow-xl shadow-green-600/20 active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed">
                                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Request Custom Quote'}
                                </button>
                                <div className="flex items-center justify-center gap-2 text-xs font-bold text-gray-400 uppercase tracking-tight">
                                    <CheckCircle className="h-4 w-4 text-green-500" /> Fast Response Guaranteed
                                </div>
                            </form>
                        </div>
                    </div>

                    {/* Stats/Info Side */}
                    <div className="lg:col-span-2 space-y-8">
                        <div className="bg-gray-900 rounded-3xl p-8 text-white shadow-2xl">
                            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                <span className="w-1 h-5 bg-green-500 rounded-full"></span>
                                Why EzyTravel?
                            </h3>
                            <div className="space-y-6">
                                {[
                                    { title: 'Best In-Class Fleet', desc: 'From luxury Audis to 50-seater premium Volvo coaches.', icon: Bus },
                                    { title: 'Verified Drivers', desc: 'Highly experienced and professionally trained pilots.', icon: Users },
                                    { title: 'Transparent Pricing', desc: 'No hidden taxes. What we quote is what you pay.', icon: CheckCircle },
                                    { title: '24/7 Support', desc: 'Personal travel manager assigned to every booking.', icon: Phone }
                                ].map((item, id) => (
                                    <div key={id} className="flex items-start gap-4">
                                        <div className="bg-white/10 p-3 rounded-xl mt-1">
                                            <item.icon className="h-5 w-5 text-green-400" />
                                        </div>
                                        <div>
                                            <p className="font-bold text-sm tracking-tight">{item.title}</p>
                                            <p className="text-gray-400 text-xs mt-1 leading-relaxed">{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="bg-green-50 border border-green-100 rounded-3xl p-8">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="bg-green-600 text-white p-2 rounded-lg">
                                   <Phone className="h-4 w-4" />
                                </div>
                                <h4 className="font-black text-gray-900 uppercase text-xs tracking-widest">Need help?</h4>
                            </div>
                            <p className="text-gray-600 text-sm leading-relaxed mb-6 font-medium">
                                Prefer talking to a human for complex trip requirements? Our travel experts are just a call away.
                            </p>
                            <a href="tel:+919400097300" className="flex items-center justify-between bg-white px-5 py-4 rounded-2xl border border-green-200 group hover:border-green-400 transition-all">
                                <span className="font-black text-gray-900 tracking-tight">+91 94000 97300</span>
                                <ArrowRight className="h-4 w-4 text-green-600 group-hover:translate-x-1 transition-transform" />
                            </a>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
